import React, { useState, useEffect, useMemo } from "react";
import { useParams, NavLink } from "react-router-dom";
import { getProductList } from "../../API/mangoholidayAPI";
import ClipLoader from "react-spinners/ClipLoader";
import { Helmet } from "react-helmet";
import "./TourPage.css";

const TourCategory = () => {
  const { category } = useParams();

  const [allTours, setAllTours] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- Filter States ---
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("All");
  const [selectedBudget, setSelectedBudget] = useState("All");
  const [selectedDuration, setSelectedDuration] = useState("All");

  // --- Mobile UX States ---
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  const formattedCategory =
    category === "group" ? "Group Tours" : "Customized Tours";

  useEffect(() => {
    document.title = `${formattedCategory} | Mango Holidays`;
  }, [formattedCategory]);

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleMobileSearch = () => {
    setIsMobileSearchOpen(!isMobileSearchOpen);
    setIsMobileFiltersOpen(false); // Close the other
  };

  const toggleMobileFilters = () => {
    setIsMobileFiltersOpen(!isMobileFiltersOpen);
    setIsMobileSearchOpen(false); // Close the other
  };

  /* ------------------------------------------------------------------
      FETCH ALL TOURS
  -------------------------------------------------------------------*/
  useEffect(() => {
    async function fetchCategoryTours() {
      try {
        setLoading(true);
        setSearchQuery("");
        setSelectedCountry("All");
        setSelectedBudget("All");
        setSelectedDuration("All");
        setIsMobileSearchOpen(false);
        setIsMobileFiltersOpen(false);

        const worldData = await getProductList({ ProductType: "World" });
        const indiaData = await getProductList({ ProductType: "India" });

        // Safely extract both lists
        const allWorldTours = worldData?.ProductList || [];
        const allIndiaTours = indiaData?.ProductList || [];

        // Combine them into one master array before filtering
        const combinedTours = [...allWorldTours, ...allIndiaTours];

        let baseTours = [];

        // Apply strict GIT / FIT filters to ALL tours combined
        if (category === "group") {
          baseTours = combinedTours.filter((item) => item.TravelType === "GIT");
        } else if (category === "customized") {
          baseTours = combinedTours.filter((item) => item.TravelType === "FIT");
        }

        const today = new Date();
        const toursWithPricing = baseTours.map((item) => {
          let matchedNetINRValue = null;

          if (Array.isArray(item.ProductPricingHeader)) {
            for (const header of item.ProductPricingHeader) {
              const fromParts = header.TourDateFrom?.split("/") || [];
              const toParts = header.TourDateTo?.split("/") || [];
              const fromDate = new Date(
                `${fromParts[2]}-${fromParts[1]}-${fromParts[0]}`,
              );
              const toDate = new Date(
                `${toParts[2]}-${toParts[1]}-${toParts[0]}`,
              );

              if (today >= fromDate && today <= toDate) {
                matchedNetINRValue =
                  header?.ProductPricingDetail?.[0]?.NETINRValue ?? null;
                break;
              }
            }
          }
          return { ...item, NETINRValue: matchedNetINRValue };
        });

        setAllTours(toursWithPricing);
      } catch (err) {
        console.error("Error loading category tours:", err);
        setAllTours([]);
      } finally {
        setLoading(false);
      }
    }

    fetchCategoryTours();
  }, [category]);

  /* ------------------------------------------------------------------
      DYNAMIC FILTER LOGIC
  -------------------------------------------------------------------*/
  const uniqueCountries = useMemo(() => {
    const countries = allTours.map((tour) => tour.SectorName).filter(Boolean);
    return ["All", ...new Set(countries)].sort();
  }, [allTours]);

  const filteredTours = useMemo(() => {
    const filtered = allTours.filter((pkg) => {
      const matchesSearch =
        pkg.ProductTitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        pkg.SectorName?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCountry =
        selectedCountry === "All" || pkg.SectorName === selectedCountry;

      const price =
        Number(pkg.LowestTwinSharingPrice) || Number(pkg.NETINRValue) || 0;
      let matchesBudget = true;
      if (selectedBudget === "Under ₹50,000")
        matchesBudget = price > 0 && price < 50000;
      else if (selectedBudget === "₹50,000 - ₹1,00,000")
        matchesBudget = price >= 50000 && price <= 100000;
      else if (selectedBudget === "₹1,00,000 - ₹2,00,000")
        matchesBudget = price > 100000 && price <= 200000;
      else if (selectedBudget === "Above ₹2,00,000")
        matchesBudget = price > 200000;

      const days = Number(pkg.Days) || 0;
      let matchesDuration = true;
      if (selectedDuration === "1 - 5 Days")
        matchesDuration = days >= 1 && days <= 5;
      else if (selectedDuration === "6 - 10 Days")
        matchesDuration = days >= 6 && days <= 10;
      else if (selectedDuration === "11+ Days") matchesDuration = days >= 11;

      return (
        matchesSearch && matchesCountry && matchesBudget && matchesDuration
      );
    });

    // Sort alphabetically A-Z
    return filtered.sort((a, b) => {
      const titleA = a.ProductTitle || "";
      const titleB = b.ProductTitle || "";
      return titleA.localeCompare(titleB);
    });
  }, [
    allTours,
    searchQuery,
    selectedCountry,
    selectedBudget,
    selectedDuration,
  ]);

  return (
    <>
      <Helmet>
        <title>{formattedCategory} | Mango Holidays</title>
        <meta
          name="description"
          content={`Explore our wide range of ${formattedCategory} across the globe.`}
        />
      </Helmet>

      {/* Banner Section */}
      <section className="banner-section-packages">
        <div className="img-container-packages">
          <img
            src={
              allTours.length > 0
                ? allTours[0].ProductImage
                : "/img/default-banner.webp"
            }
            alt={formattedCategory}
          />
        </div>
        <div className="banner-overlay-packages">
          <h1 className="banner-title-packages">{formattedCategory}</h1>
        </div>
      </section>

      {/* --- FILTERS SECTION --- */}
      <section className="tour-filters-section">
        <div className="filters-wrapper">
          {/* Mobile UX Controls */}
          <div className="mobile-controls">
            <button
              className={`control-btn ${isMobileSearchOpen ? "active" : ""}`}
              onClick={toggleMobileSearch}
            >
              <i className="fa-solid fa-magnifying-glass"></i> Search
            </button>
            <button
              className={`control-btn ${isMobileFiltersOpen ? "active" : ""}`}
              onClick={toggleMobileFilters}
            >
              <i className="fa-solid fa-sliders"></i> Filters
            </button>
          </div>

          {/* Filters Container (Grid on desktop, animated accordion on mobile) */}
          <div className="filters-container">
            {/* Search Input Block */}
            <div
              className={`filter-block search-block ${isMobileSearchOpen ? "mobile-show" : ""}`}
            >
              <div className="filter-group search-group">
                <i className="fa-solid fa-magnifying-glass"></i>
                <input
                  type="text"
                  placeholder="Search destinations, tours..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Dropdowns Block */}
            <div
              className={`filter-block dropdown-block ${isMobileFiltersOpen ? "mobile-show" : ""}`}
            >
              <div className="filter-group">
                <select
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                >
                  {uniqueCountries.map((country, idx) => (
                    <option key={idx} value={country}>
                      {country === "All" ? "All Destinations" : country}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <select
                  value={selectedBudget}
                  onChange={(e) => setSelectedBudget(e.target.value)}
                >
                  <option value="All">Any Budget</option>
                  <option value="₹1,00,000 - ₹2,00,000">
                    ₹1,00,000 - ₹2,00,000
                  </option>
                  <option value="₹2,00,000 - ₹3,00,000">
                    ₹2,00,000 - ₹3,00,000
                  </option>
                  <option value="₹2,00,000 - ₹3,00,000">
                    ₹3,00,000 - ₹4,00,000
                  </option>
                  <option value="₹2,00,000 - ₹3,00,000">
                    ₹4,00,000 - ₹5,00,000
                  </option>
                  <option value="Above ₹5,00,000">Above ₹5,00,000</option>
                </select>
              </div>

              <div className="filter-group">
                <select
                  value={selectedDuration}
                  onChange={(e) => setSelectedDuration(e.target.value)}
                >
                  <option value="All">Any Duration</option>
                  <option value="1 - 5 Days">1 - 5 Days</option>
                  <option value="6 - 10 Days">6 - 10 Days</option>
                  <option value="11+ Days">11+ Days</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Packages Grid Section */}
      <section className="packages-cards-section">
        <div className="container">
          {loading ? (
            <div className="loader-container">
              <ClipLoader color="#ff5f10" size={100} />
              <p className="loading-text">Fetching {formattedCategory}...</p>
            </div>
          ) : filteredTours.length > 0 ? (
            <div className="packages-grid">
              {filteredTours.map((pkg) => (
                <div key={pkg.ProductCode} className="package-image-container">
                  <div className="package-image">
                    <img src={pkg.ProductImage} alt={pkg.ProductTitle} />
                  </div>
                  <div className="hover-overlay">
                    <i className="fas fa-link fa-2x"></i>
                  </div>
                  <div className="package-overlay-content">
                    <h3>{pkg.ProductTitle}</h3>
                    <p>
                      {pkg.Days} days | {pkg.Nights} nights
                    </p>
                    <div className="price-bar">
                      <h3>
                        {pkg.LowestTwinSharingPrice
                          ? `₹ ${Number(pkg.LowestTwinSharingPrice).toLocaleString("en-IN")}`
                          : pkg.NETINRValue
                            ? `₹ ${Number(pkg.NETINRValue).toLocaleString("en-IN")}`
                            : "Price on Request"}
                      </h3>
                      <NavLink
                        to={`/tour-details/${pkg.ProductID}/${pkg.ProductCode}/${pkg.ProductTitle.toLowerCase().replace(/\s+/g, "-")}`}
                        state={{ productImg: pkg.ProductImage }}
                        onClick={handleScrollToTop}
                      >
                        <p>See Details</p>
                      </NavLink>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="data-unavailable-banner">
              <h2
                style={{
                  textAlign: "center",
                  color: "#666",
                  width: "100%",
                  padding: "50px",
                }}
              >
                No tours match your current filters. Try adjusting your search!
              </h2>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default TourCategory;
